import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaTimes, FaFileAlt } from "react-icons/fa";
import { Pencil, Trash2, Upload } from "lucide-react";
import api from "../../../services/apiService";
import DataTable, { createActionsColumn, DataTableButton } from "../../../components/data-table";
import AlertNotification from "../../../components/notification";
import DeleteModal from "../../../components/models/deleteModal.jsx";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const DOC_TYPES = ["cnic","degree","contract","offer_letter","certificate","experience_letter","other"];

function DocModal({ show, onClose, onSubmit, initial = null, isSubmitting, error }) {
  const [docType,    setDocType]    = useState(initial?.documentType || "");
  const [title,      setTitle]      = useState(initial?.title        || "");
  const [expiryDate, setExpiryDate] = useState(initial?.expiryDate   || "");
  const [notes,      setNotes]      = useState(initial?.notes        || "");
  const [file,       setFile]       = useState(null);
  const fileRef = useRef();

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!docType) return;
    onSubmit({ documentType: docType, title, expiryDate, notes, file });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 dark:bg-black/60">
      <button type="button" onClick={onClose} className="absolute inset-0" />
      <div className="relative w-full max-w-lg rounded-2xl border border-app-primary bg-app-card shadow-2xl dark:shadow-black/50">
        <div className="flex items-center justify-between border-b border-app-primary px-6 py-4">
          <h3 className="text-lg font-semibold text-app-heading">{initial ? "Edit Document" : "Add Document"}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-app-muted transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"><FaTimes /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">{error}</p>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="doc-type" className="mb-1 block text-sm font-medium text-app-secondary">Type <span className="text-red-500 dark:text-red-400">*</span></label>
              <select id="doc-type" required value={docType} onChange={(e) => setDocType(e.target.value)}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20">
                <option value="">— Select —</option>
                {DOC_TYPES.map((t) => <option key={t} value={t}>{t.replace("_"," ")}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="doc-title" className="mb-1 block text-sm font-medium text-app-secondary">Title</label>
              <input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20" />
            </div>
            <div>
              <label htmlFor="doc-expiry" className="mb-1 block text-sm font-medium text-app-secondary">Expiry Date</label>
              <input id="doc-expiry" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20" />
            </div>
            <div className="col-span-2">
              <label htmlFor="doc-file" className="mb-1 block text-sm font-medium text-app-secondary">
                File {initial ? "(leave empty to keep existing)" : ""}
              </label>
              <input id="doc-file" ref={fileRef} type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-secondary file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-1 file:text-xs file:font-medium file:text-[#2470B7] file:transition-colors hover:file:bg-blue-100 dark:file:bg-blue-500/15 dark:file:text-blue-300 dark:hover:file:bg-blue-500/25" />
              {initial?.fileName && !file && (
                <p className="mt-1 flex items-center gap-1 text-xs text-app-muted">
                  <FaFileAlt className="shrink-0" /> {initial.fileName}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <label htmlFor="doc-notes" className="mb-1 block text-sm font-medium text-app-secondary">Notes</label>
              <textarea id="doc-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20 resize-none" />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-app-primary pt-4">
            <button type="button" onClick={onClose}
              className="rounded-lg border border-app-primary px-5 py-2 text-sm font-semibold text-app-secondary transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40">Cancel</button>
            <button type="submit" disabled={isSubmitting}
              className="rounded-lg bg-[#2470B7] border border-[#2470B7] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500">
              {isSubmitting ? "Uploading…" : initial ? "Save Changes" : "Add Document"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HrEmployeeDocuments({ employeeId }) {
  const [docs, setDocs]             = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting]     = useState(false);

  const fetchDocs = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/hr/employees/${employeeId}/documents`, { headers: authHeaders() });
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch { setNotification({ text: "Failed to load documents.", status: false }); }
    finally { setIsLoading(false); }
  }, [employeeId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleSubmit = async ({ documentType, title, expiryDate, notes, file }) => {
    setIsSubmitting(true); setSubmitError("");
    try {
      const fd = new FormData();
      fd.append("documentType", documentType);
      if (title)      fd.append("title",      title);
      if (expiryDate) fd.append("expiryDate", expiryDate);
      if (notes)      fd.append("notes",      notes);
      if (file)       fd.append("file",       file);

      const headers = { ...authHeaders() };

      if (editing) {
        await api.put(`/hr/employees/${employeeId}/documents/${editing.id}`, fd, { headers });
      } else {
        await api.post(`/hr/employees/${employeeId}/documents`, fd, { headers });
      }
      setShowModal(false); setEditing(null);
      setNotification({ text: editing ? "Document updated." : "Document uploaded.", status: true });
      fetchDocs();
    } catch (err) { setSubmitError(err?.response?.data?.message || "Failed to save."); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/hr/employees/${employeeId}/documents/${deleteTarget.id}`, { headers: authHeaders() });
      setNotification({ text: "Document deleted.", status: true });
      setDeleteTarget(null); fetchDocs();
    } catch { setNotification({ text: "Failed to delete.", status: false }); }
    finally { setIsDeleting(false); }
  };

  const today = new Date().toISOString().split("T")[0];

  const columns = [
    { key: "documentType", label: "Type", sortable: true, searchable: true,
      accessor: (doc) => doc.documentType?.replace("_", " "),
      render: (v) => <span className="capitalize">{v || "—"}</span> },
    { key: "title", label: "Title", sortable: true, searchable: true,
      render: (v) => v || "—" },
    { key: "fileName", label: "File", searchable: true,
      render: (v, doc) => doc.fileUrl ? (
        <a href={doc.fileUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded text-xs text-[#2470B7] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:text-blue-400 dark:hover:text-blue-300">
          <FaFileAlt className="shrink-0" />
          {v || "View"}
        </a>
      ) : "—" },
    { key: "expiryDate", label: "Expiry", sortable: true,
      render: (v) => (
        <span className={`text-xs ${v && v <= today ? "font-semibold text-red-600 dark:text-red-400" : ""}`}>{v || "—"}</span>
      ) },
    { key: "uploader.email", label: "Uploaded By", sortable: true, searchable: true,
      render: (v) => <span className="text-xs text-app-muted">{v || "—"}</span> },
    createActionsColumn((doc) => [
      { label: "Edit", icon: Pencil, onSelect: () => { setEditing(doc); setSubmitError(""); setShowModal(true); } },
      { label: "Delete", icon: Trash2, tone: "danger", onSelect: () => setDeleteTarget(doc) },
    ], { getRowLabel: (doc) => doc.title || doc.fileName || `document ${doc.id}` }),
  ];

  return (
    <div className="space-y-4">
      <DataTable
        ariaLabel="Employee documents"
        columns={columns}
        data={docs}
        loading={isLoading}
        searchable
        searchPlaceholder="Search type, title or file"
        toolbarLeft={<h3 className="text-base font-semibold text-app-heading">Documents</h3>}
        toolbarRight={
          <DataTableButton variant="primary" icon={Upload} onClick={() => { setEditing(null); setSubmitError(""); setShowModal(true); }}>
            Upload Document
          </DataTableButton>
        }
        pagination
        defaultPageSize={10}
        labels={{ empty: "No documents uploaded yet." }}
      />

      {notification && <AlertNotification text={notification.text} status={notification.status} onClose={() => setNotification(null)} />}
      <DocModal key={editing?.id ?? "new"} show={showModal}
        onClose={() => { if (!isSubmitting) { setShowModal(false); setEditing(null); } }}
        onSubmit={handleSubmit} initial={editing} isSubmitting={isSubmitting} error={submitError} />
      <DeleteModal show={Boolean(deleteTarget)} handleClose={() => { if (!isDeleting) setDeleteTarget(null); }}
        handleConfirm={handleDelete} message="Delete this document? The file will be removed from storage." loading={isDeleting} />
    </div>
  );
}

export default HrEmployeeDocuments;
